import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaChevronDown } from "react-icons/fa";
import { handlePhoneNumberChange } from "../utils/phoneUtils";
import axiosInstance from "../config/axios";
import { toast } from "react-toastify";

/** Payload for POST/PUT /outside-brokers — normalizes enums so empty client state cannot 400 the API */
function buildOutsideBrokerApiPayload(broker, selectedBrokerType) {
  const legacyAddressLine =
    `${String(broker.streetNumber || "").trim()} ${String(
      broker.streetName || "",
    ).trim()}${
      String(broker.unitNumber || "").trim()
        ? `, ${String(broker.unitNumber || "").trim()}`
        : ""
    }`.trim();

  return {
    type: selectedBrokerType,
    firstName: broker.firstName || "",
    lastName: broker.lastName || "",
    company: broker.company || "",
    email: broker.email || "",
    payBroker: broker.payBroker === "Yes" ? "Yes" : "No",
    end:
      selectedBrokerType === "Listing Broker" ? "Listing End" : "Selling End",
    primaryPhone: broker.primaryPhone || "",
    chargedHST: broker.chargedHST === "No" ? "No" : "Yes",
    streetNumber: broker.streetNumber || "",
    streetName: broker.streetName || "",
    unitNumber: broker.unitNumber || "",
    province: broker.province || "",
    postalCode: broker.postalCode || "",
    address: legacyAddressLine || broker.address || "",
  };
}

const OutsideBrokersForm = ({
  outsideBrokers,
  setOutsideBrokers,
  referralAgents = [],
  editingReferralGlobalIndex = null,
  onEditReferralAgent,
  onUpdateReferralAgent,
  onCancelEditReferral,
  onDeleteReferralAgent,
  broker,
  setBroker,
  selectedBrokerType,
  setSelectedBrokerType,
  editingBrokerIndex,
  setEditingBrokerIndex,
  showDeleteBrokerConfirm,
  handleBrokerInputChange,
  handleBrokerEdit,
  handleBrokerDelete,
  confirmDeleteBroker,
  cancelDeleteBroker,
  goToNextSection,
  goToPreviousSection,
}) => {
  const MAX_COOPERATING_BROKERS = 5;

  const getCooperatingCountExcludingEditing = () => {
    return (outsideBrokers || []).filter((b, idx) => {
      if (editingBrokerIndex !== null && idx === editingBrokerIndex)
        return false;
      return (b?.type || "") === "Cooperating Broker";
    }).length;
  };

  const assertCanAddBrokerType = (type) => {
    if (type !== "Cooperating Broker") return true;
    const count = getCooperatingCountExcludingEditing();
    if (count >= MAX_COOPERATING_BROKERS) {
      toast.error(
        `You can add maximum ${MAX_COOPERATING_BROKERS} Co-operating Brokers.`,
      );
      return false;
    }
    return true;
  };

  // State for company dropdown
  const [companies, setCompanies] = useState([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Debug outsideBrokers prop changes
  useEffect(() => {
    console.log(
      "OutsideBrokersForm received new outsideBrokers prop:",
      outsideBrokers,
    );
  }, [outsideBrokers]);

  // When editing a referral agent, sync company search term with broker.company
  useEffect(() => {
    if (selectedBrokerType === "Referral" && broker.company) {
      setCompanySearchTerm(broker.company);
    }
  }, [selectedBrokerType, broker.company]);

  // Fetch companies from outside brokers database
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const response = await axiosInstance.get("/outside-brokers");
      // Extract unique companies; when same company appears in multiple brokers, use the one with the most complete address
      const uniqueCompanies = response.data.reduce((acc, broker) => {
        if (!broker.company) return acc;
        const existing = acc.find((c) => c.name === broker.company);
        const data = {
          name: broker.company,
          address: broker.address || "",
          streetNumber: broker.streetNumber || "",
          streetName: broker.streetName || "",
          unitNumber: broker.unitNumber || "",
          province: broker.province || "",
          postalCode: broker.postalCode || "",
          email: broker.email || "",
          phone: broker.primaryPhone || "",
        };
        if (!existing) {
          acc.push(data);
        } else {
          // Merge: keep existing value unless this broker has a non-empty value for that field
          [
            "address",
            "streetNumber",
            "streetName",
            "unitNumber",
            "province",
            "postalCode",
            "email",
            "phone",
          ].forEach((key) => {
            if (data[key] && !existing[key]) existing[key] = data[key];
          });
        }
        return acc;
      }, []);
      setCompanies(uniqueCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to fetch companies");
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleCompanySelect = (company) => {
    setBroker((prev) => ({
      ...prev,
      company: company.name,
      streetNumber: company.streetNumber || "",
      streetName: company.streetName || "",
      unitNumber: company.unitNumber || "",
      province: company.province || "",
      postalCode: company.postalCode || "",
      // Keep legacy address string as fallback
      address: company.address || "",
      email: company.email || "",
      primaryPhone: company.phone || "",
    }));
    setCompanySearchTerm(company.name);
    setShowCompanyDropdown(false);
  };

  const handleCompanyInputChange = (e) => {
    const value = e.target.value;
    setCompanySearchTerm(value);
    setBroker((prev) => ({ ...prev, company: value }));
    setShowCompanyDropdown(value.length > 0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCompanyDropdown &&
        !event.target.closest(".company-dropdown-container")
      ) {
        setShowCompanyDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCompanyDropdown]);

  // Populate form when editing a broker
  useEffect(() => {
    if (editingBrokerIndex !== null && outsideBrokers[editingBrokerIndex]) {
      const brokerToEdit = outsideBrokers[editingBrokerIndex];
      const brokerType = brokerToEdit.type || "";
      setBroker({
        type: brokerType,
        firstName: brokerToEdit.firstName || "",
        lastName: brokerToEdit.lastName || "",
        company: brokerToEdit.company || "",
        email: brokerToEdit.email || "",
        payBroker: brokerToEdit.payBroker || "No",
        end: brokerType === "Listing Broker" ? "Listing End" : "Selling End",
        primaryPhone: brokerToEdit.primaryPhone || "",
        chargedHST: brokerToEdit.chargedHST || "Yes",
        streetNumber: brokerToEdit.streetNumber || "",
        streetName: brokerToEdit.streetName || "",
        unitNumber: brokerToEdit.unitNumber || "",
        province: brokerToEdit.province || "",
        postalCode: brokerToEdit.postalCode || "",
        // Backward compat
        address: brokerToEdit.address || "",
      });
      setSelectedBrokerType(brokerType);
      setCompanySearchTerm(brokerToEdit.company || "");
    }
  }, [editingBrokerIndex, outsideBrokers]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!assertCanAddBrokerType(selectedBrokerType)) return;

    const formData = buildOutsideBrokerApiPayload(broker, selectedBrokerType);

    console.log("OutsideBrokersForm handleFormSubmit called");
    console.log("Current outside brokers:", outsideBrokers);
    console.log("Form data:", formData);
    console.log("Editing index:", editingBrokerIndex);

    // Update referral agent (edit in place under Outside Brokers), including address
    if (selectedBrokerType === "Referral") {
      if (
        onUpdateReferralAgent &&
        editingReferralGlobalIndex != null
      ) {
      const legacyAddress =
        [
          broker.streetNumber,
          broker.streetName,
          broker.unitNumber,
          broker.province,
          broker.postalCode,
        ]
          .filter(Boolean)
          .join(", ") ||
        broker.address ||
        "";
      onUpdateReferralAgent(editingReferralGlobalIndex, {
        firstName: broker.firstName || "",
        lastName: broker.lastName || "",
        company: broker.company || "",
        email: broker.email || "",
        primaryPhone: broker.primaryPhone || "",
        chargedHST: broker.chargedHST === "No" ? "No" : "Yes",
        streetNumber: broker.streetNumber || "",
        streetName: broker.streetName || "",
        unitNumber: broker.unitNumber || "",
        province: broker.province || "",
        postalCode: broker.postalCode || "",
        address: legacyAddress,
      });
      setCompanySearchTerm("");
      }
      return;
    }

    try {
      if (editingBrokerIndex !== null) {
        // Update existing broker in database
        const brokerToUpdate = outsideBrokers[editingBrokerIndex];
        if (brokerToUpdate._id) {
          await axiosInstance.put(
            `/outside-brokers/${brokerToUpdate._id}`,
            formData,
          );
        }

        // Update local state
        const updatedBrokers = [...outsideBrokers];
        updatedBrokers[editingBrokerIndex] = {
          ...formData,
          _id: brokerToUpdate._id,
        };
        setOutsideBrokers(updatedBrokers);
        setEditingBrokerIndex(null);
        toast.success("Broker updated successfully");
      } else {
        // Add new broker to database
        const response = await axiosInstance.post("/outside-brokers", formData);
        const newBroker = response.data;

        // Update local state
        const newBrokers = [...outsideBrokers, newBroker];
        setOutsideBrokers(newBrokers);
        toast.success("Broker added successfully");
      }
    } catch (error) {
      console.error("Error saving broker:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save broker";
      toast.error(typeof msg === "string" ? msg : "Failed to save broker");
    }

    // Reset form
    setBroker({
      type: "",
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      payBroker: "No",
      end: "Listing End",
      primaryPhone: "",
      chargedHST: "Yes",
      streetNumber: "",
      streetName: "",
      unitNumber: "",
      province: "",
      postalCode: "",
      address: "",
    });
    setSelectedBrokerType("");
    setCompanySearchTerm("");
  };

  // Simple broker type selection handler
  const handleLocalBrokerTypeSelect = (type) => {
    setSelectedBrokerType(type);
    setBroker((prev) => ({
      ...prev,
      type: type,
      end: type === "Listing Broker" ? "Listing End" : "Selling End",
    }));
  };

  const handleAddNew = async () => {
    // First, add the current broker data to the table if it's valid (First Name and Last Name are optional)
    if (selectedBrokerType) {
      if (selectedBrokerType === "Referral") return;
      if (!assertCanAddBrokerType(selectedBrokerType)) return;

      const formData = buildOutsideBrokerApiPayload(
        broker,
        selectedBrokerType,
      );

      try {
        // Save to database
        const response = await axiosInstance.post("/outside-brokers", formData);
        const newBroker = response.data;

        // Update local state
        setOutsideBrokers([...outsideBrokers, newBroker]);
        toast.success("Broker added successfully");
      } catch (error) {
        console.error("Error saving broker:", error);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save broker";
        toast.error(typeof msg === "string" ? msg : "Failed to save broker");
      }
    }

    // Reset form
    setEditingBrokerIndex(null);
    setSelectedBrokerType("");
    setBroker({
      type: "",
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      payBroker: "No",
      end: "Listing End",
      primaryPhone: "",
      chargedHST: "Yes",
      streetNumber: "",
      streetName: "",
      unitNumber: "",
      province: "",
      postalCode: "",
      address: "",
    });
    setCompanySearchTerm("");
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-6">Outside Brokers Information</h2>

      {editingReferralGlobalIndex == null && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 text-lg mb-2">
            Select Broker Type
          </h3>
          <div className="flex flex-wrap gap-3">
            {["Listing Broker", "Cooperating Broker"].map((type) => (
              <div key={type} className="flex items-center">
                <input
                  type="radio"
                  id={`broker-type-${type}`}
                  name="brokerType"
                  checked={selectedBrokerType === type}
                  onChange={() => handleLocalBrokerTypeSelect(type)}
                  className="mr-2"
                />
                <label
                  htmlFor={`broker-type-${type}`}
                  className="text-sm font-medium text-gray-700"
                >
                  {type}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedBrokerType === "Referral" && (
        <div className="mb-4">
          <h3 className="font-medium text-gray-700 text-lg mb-2">
            Edit Referral Agent
          </h3>
        </div>
      )}
      {selectedBrokerType && (
        <form
          onSubmit={handleFormSubmit}
          className="space-y-4 p-4 border rounded-lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <input
                type="text"
                name="type"
                defaultValue={selectedBrokerType}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pay Broker
              </label>
              <select
                name="payBroker"
                value={broker.payBroker}
                onChange={handleBrokerInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End
              </label>
              <input
                type="text"
                name="end"
                value={
                  selectedBrokerType === "Referral"
                    ? "Selling End"
                    : selectedBrokerType === "Listing Broker"
                      ? "Listing End"
                      : "Selling End"
                }
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={broker.firstName || ""}
                onChange={handleBrokerInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={broker.lastName || ""}
                onChange={handleBrokerInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              />
            </div>
          </div>
          <div className="relative company-dropdown-container">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <div className="relative">
              <input
                type="text"
                name="company"
                value={companySearchTerm || broker.company || ""}
                onChange={handleCompanyInputChange}
                onFocus={() => setShowCompanyDropdown(true)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm pr-8"
                placeholder="Type to search or enter company name"
              />
              <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Company Dropdown */}
            {showCompanyDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {isLoadingCompanies ? (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    Loading companies...
                  </div>
                ) : companies.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    No companies found
                  </div>
                ) : (
                  companies
                    .filter((company) =>
                      company.name
                        .toLowerCase()
                        .includes(companySearchTerm.toLowerCase()),
                    )
                    .map((company, index) => (
                      <div
                        key={index}
                        onClick={() => handleCompanySelect(company)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {company.name}
                        </div>
                        {company.address && (
                          <div className="text-xs text-gray-500">
                            {company.address}
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={broker.email || ""}
              onChange={handleBrokerInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Phone Number
              </label>
              <input
                type="tel"
                name="primaryPhone"
                value={broker.primaryPhone || ""}
                onChange={(e) => handlePhoneNumberChange(e, setBroker)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Charged HST
              </label>
              <select
                name="chargedHST"
                value={broker.chargedHST}
                onChange={handleBrokerInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Street #
                </label>
                <input
                  type="text"
                  name="streetNumber"
                  value={broker.streetNumber || ""}
                  onChange={handleBrokerInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Street Name
                </label>
                <input
                  type="text"
                  name="streetName"
                  value={broker.streetName || ""}
                  onChange={handleBrokerInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Unit #
                </label>
                <input
                  type="text"
                  name="unitNumber"
                  value={broker.unitNumber || ""}
                  onChange={handleBrokerInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Province
                </label>
                <input
                  type="text"
                  name="province"
                  value={broker.province || ""}
                  onChange={handleBrokerInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Postal Code
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={broker.postalCode || ""}
                  onChange={handleBrokerInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700 sm:text-sm"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            {selectedBrokerType === "Referral" ? (
              <>
                {onCancelEditReferral && (
                  <button
                    type="button"
                    onClick={() => {
                      onCancelEditReferral();
                      setCompanySearchTerm("");
                    }}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
                >
                  Update Referral Agent
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
                >
                  Add New
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
                >
                  {editingBrokerIndex !== null ? "Update Broker" : "Add Broker"}
                </button>
              </>
            )}
          </div>
        </form>
      )}

      {/* Outside Brokers Table (includes referral agents from Agent Info) */}
      {(outsideBrokers.length > 0 || referralAgents.length > 0) && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Added Outside Brokers</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 border-b text-left">Type</th>
                  <th className="px-4 py-2 border-b text-left">First Name</th>
                  <th className="px-4 py-2 border-b text-left">Last Name</th>
                  <th className="px-4 py-2 border-b text-left">Company</th>
                  <th className="px-4 py-2 border-b text-left">End</th>
                  <th className="px-4 py-2 border-b text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {outsideBrokers.map((broker, index) => (
                  <tr key={`ob-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">{broker.type || "-"}</td>
                    <td className="px-4 py-2 border-b">
                      {broker.firstName || "-"}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {broker.lastName || "-"}
                    </td>
                    <td className="px-4 py-2 border-b">
                      {broker.company || "-"}
                    </td>
                    <td className="px-4 py-2 border-b">{broker.end || "-"}</td>
                    <td className="px-4 py-2 border-b text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleBrokerEdit(index)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleBrokerDelete(index)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {referralAgents.map(({ agent, globalIndex }, index) => (
                    <tr
                      key={`ref-${index}`}
                      className="hover:bg-gray-50 bg-blue-50/50"
                    >
                      <td className="px-4 py-2 border-b">Referral</td>
                      <td className="px-4 py-2 border-b">-</td>
                      <td className="px-4 py-2 border-b">-</td>
                      <td className="px-4 py-2 border-b">
                        {agent.agentName || "-"}
                      </td>
                      <td className="px-4 py-2 border-b">Selling End</td>
                      <td className="px-4 py-2 border-b text-center">
                        <div className="flex justify-center space-x-2">
                          {onEditReferralAgent && (
                            <button
                              type="button"
                              onClick={() => onEditReferralAgent(globalIndex)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit (in Agent Info)"
                            >
                              <FaEdit />
                            </button>
                          )}
                          {onDeleteReferralAgent && (
                            <button
                              type="button"
                              onClick={() => onDeleteReferralAgent(globalIndex)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteBrokerConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="mb-6">Are you sure you want to delete this broker?</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteBroker}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBroker}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={goToPreviousSection}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={goToNextSection}
          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default OutsideBrokersForm;
